import { useState, useEffect, useCallback } from 'react';

// Check if WebAuthn/Biometrics is available
export const isBiometricsAvailable = async () => {
  if (!window.PublicKeyCredential) {
    return { available: false, reason: 'WebAuthn not supported' };
  }
  
  try {
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return { 
      available, 
      reason: available ? 'Biometrics available' : 'No platform authenticator'
    };
  } catch (error) {
    return { available: false, reason: error.message };
  }
};

// Generate a random challenge
const generateChallenge = () => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return array;
};

// Convert ArrayBuffer to Base64
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// Convert Base64 to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Hook for biometric authentication
export const useBiometrics = () => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check availability and if user has enabled biometrics
  useEffect(() => {
    const checkBiometrics = async () => {
      try {
        const { available } = await isBiometricsAvailable();
        setIsAvailable(available);
        
        // Check if user has registered biometrics
        const credentialId = localStorage.getItem('vitala_biometric_credential');
        setIsEnabled(available && !!credentialId);
      } catch (err) {
        console.error('Biometrics check failed:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkBiometrics();
  }, []);

  // Register biometric credential
  const enableBiometrics = useCallback(async (userId, userName) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const challenge = generateChallenge();
      
      const publicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: "Vitala",
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(userId),
          name: userName,
          displayName: userName,
        },
        pubKeyCredParams: [
          { alg: -7, type: "public-key" },   // ES256
          { alg: -257, type: "public-key" }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required",
          residentKey: "preferred",
        },
        timeout: 60000,
        attestation: "none",
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      });

      // Store credential ID for future authentication
      const credentialId = arrayBufferToBase64(credential.rawId);
      localStorage.setItem('vitala_biometric_credential', credentialId);
      localStorage.setItem('vitala_biometric_user', userId);
      
      setIsEnabled(true);
      return { success: true };
    } catch (err) {
      console.error('Biometric registration failed:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Authenticate with biometrics
  const authenticateWithBiometrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const credentialId = localStorage.getItem('vitala_biometric_credential');
      if (!credentialId) {
        throw new Error('No biometric credential registered');
      }

      const challenge = generateChallenge();
      
      const publicKeyCredentialRequestOptions = {
        challenge,
        allowCredentials: [{
          id: base64ToArrayBuffer(credentialId),
          type: "public-key",
          transports: ["internal"],
        }],
        userVerification: "required",
        timeout: 60000,
      };

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      });

      if (assertion) {
        const userId = localStorage.getItem('vitala_biometric_user');
        return { success: true, userId };
      }
      
      return { success: false, error: 'Authentication failed' };
    } catch (err) {
      console.error('Biometric authentication failed:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Disable biometrics
  const disableBiometrics = useCallback(() => {
    localStorage.removeItem('vitala_biometric_credential');
    localStorage.removeItem('vitala_biometric_user');
    setIsEnabled(false);
  }, []);

  return {
    isAvailable,
    isEnabled,
    isLoading,
    error,
    enableBiometrics,
    authenticateWithBiometrics,
    disableBiometrics,
  };
};

export default useBiometrics;
